// src/components/chat/ChatSettingsModal.jsx — Settings, Roast Consent, & Admin Bot Controls
import React, { useState } from "react";
import { X, Flame, Volume2, Bot, Shield, Sparkles } from "lucide-react";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";

export default function ChatSettingsModal({ show, onClose }) {
  const { user } = useAuth();
  const {
    userPrefs,
    updatePrefs,
    botSettings,
    updateBotConfig,
    buddySettings,
    updateBuddyConfig,
    soundEnabled,
    toggleSound
  } = useChat();

  const [roastOptIn, setRoastOptIn] = useState(Boolean(userPrefs.roast_opt_in));
  const [buddyPersona, setBuddyPersona] = useState(buddySettings?.persona || "kind");

  // Admin Bot State
  const [botEnabled, setBotEnabled] = useState(Boolean(botSettings?.enabled));
  const [botPersonality, setBotPersonality] = useState(botSettings?.personality || "tapori");
  const [savageLevel, setSavageLevel] = useState(botSettings?.savage_level || "medium");
  const [roastEnabled, setRoastEnabled] = useState(Boolean(botSettings?.roast_enabled));
  const [maxMsgsHour, setMaxMsgsHour] = useState(botSettings?.max_bot_msgs_per_user_per_hour || 20);

  if (!show) return null;

  const handleSaveUserPrefs = async () => {
    await updatePrefs({ roast_opt_in: roastOptIn });
    if (buddySettings && buddyPersona !== buddySettings.persona) {
      await updateBuddyConfig({ ...buddySettings, persona: buddyPersona });
    }
  };

  const handleSaveAdminBot = async () => {
    await updateBotConfig({
      enabled: botEnabled,
      personality: botPersonality,
      savage_level: savageLevel,
      roast_enabled: roastEnabled,
      max_bot_msgs_per_user_per_hour: Number(maxMsgsHour)
    });
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0, 0, 0, 0.7)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px"
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          boxShadow: "0 24px 64px rgba(0, 0, 0, 0.6)",
          overflow: "hidden",
          color: "var(--text)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--surface-2)"
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, fontSize: "15px" }}>
            <Bot size={18} className="text-primary" /> Chat & Assistant Settings
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              padding: "4px"
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "20px", maxHeight: "70vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "20px" }}>
          
          {/* 1. Roast Consent Toggle */}
          <div style={{ background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "12px", padding: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, fontSize: "13.5px" }}>
                <Flame size={16} style={{ color: "#ff9f0a" }} /> PlatinumBot Roast Opt-In
              </div>
              <input
                type="checkbox"
                checked={roastOptIn}
                onChange={(e) => setRoastOptIn(e.target.checked)}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "var(--primary)" }}
              />
            </div>
            <p style={{ margin: 0, fontSize: "12px", color: "var(--muted)", lineHeight: 1.45 }}>
              Allow PlatinumBot to include you in light-hearted, playful work banters when teammates use <code>/roast @yourname</code>. 
              Roasts are strictly work-related (chai, slow typing, car auctions). You can opt out anytime with immediate effect.
            </p>
          </div>

          {/* 2. Notification Sound Toggle */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 4px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Volume2 size={16} className="text-muted" />
              <div>
                <div style={{ fontSize: "13px", fontWeight: 600 }}>Message & Reminder Chimes</div>
                <div style={{ fontSize: "11.5px", color: "var(--muted)" }}>Gentle synthetic audio alert on incoming messages</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={soundEnabled}
              onChange={toggleSound}
              style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "var(--primary)" }}
            />
          </div>

          {/* 3. Buddy Persona */}
          <div>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", fontWeight: 600, marginBottom: "8px" }}>
              <Sparkles size={14} className="text-primary" /> Buddy Assistant Tone
            </label>
            <select
              value={buddyPersona}
              onChange={(e) => setBuddyPersona(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "8px",
                background: "var(--surface-2)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                fontSize: "13px"
              }}
            >
              <option value="kind">Kind & Professional (Urdu/English blend)</option>
              <option value="tapori_lite">Tapori Lite (Friendly Karachi Dhaba style)</option>
              <option value="english_chill">English Chill (Direct, concise executive)</option>
            </select>
          </div>

          {/* 4. Admin Only: PlatinumBot Controls */}
          {user?.role === "Admin" && (
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 700, color: "var(--primary)", marginBottom: "12px" }}>
                <Shield size={14} /> Admin Controls: PlatinumBot Engine
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12.5px" }}>Enable PlatinumBot in Channels</span>
                  <input
                    type="checkbox"
                    checked={botEnabled}
                    onChange={(e) => setBotEnabled(e.target.checked)}
                    style={{ width: "16px", height: "16px", accentColor: "var(--primary)" }}
                  />
                </div>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "12.5px" }}>Enable /roast Command Team-wide</span>
                  <input
                    type="checkbox"
                    checked={roastEnabled}
                    onChange={(e) => setRoastEnabled(e.target.checked)}
                    style={{ width: "16px", height: "16px", accentColor: "var(--primary)" }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: "12px", color: "var(--muted)", display: "block", marginBottom: "4px" }}>
                    Bot Personality Mode
                  </label>
                  <select
                    value={botPersonality}
                    onChange={(e) => setBotPersonality(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      fontSize: "12.5px",
                      marginBottom: "8px"
                    }}
                  >
                    <option value="tapori">Karachi Tapori (Roman Urdu Street Style)</option>
                    <option value="friendly">Friendly Assistant</option>
                    <option value="hype">Hype / Motivational Pep</option>
                    <option value="english_chill">English Chill</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: "12px", color: "var(--muted)", display: "block", marginBottom: "4px" }}>
                    Savage Level (Roast Aggressiveness)
                  </label>
                  <select
                    value={savageLevel}
                    onChange={(e) => setSavageLevel(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      fontSize: "12.5px"
                    }}
                  >
                    <option value="mild">Mild (Gentle & soft)</option>
                    <option value="medium">Medium (Standard banter)</option>
                    <option value="full">Full (Aggressive Karachi street roast)</option>
                    <option value="extreme">Extreme / Unhinged (Maximum savage burns)</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: "12px", color: "var(--muted)", display: "block", marginBottom: "4px" }}>
                    Hourly Rate Limit Per User
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    value={maxMsgsHour}
                    onChange={(e) => setMaxMsgsHour(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "6px 10px",
                      borderRadius: "6px",
                      background: "var(--surface-2)",
                      color: "var(--text)",
                      border: "1px solid var(--border)",
                      fontSize: "12.5px"
                    }}
                  />
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            background: "var(--surface-2)"
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "7px 14px",
              borderRadius: "8px",
              background: "transparent",
              color: "var(--muted)",
              border: "1px solid var(--border)",
              fontSize: "12.5px",
              cursor: "pointer"
            }}
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              await handleSaveUserPrefs();
              if (user?.role === "Admin") await handleSaveAdminBot();
              onClose();
            }}
            style={{
              padding: "7px 16px",
              borderRadius: "8px",
              background: "var(--primary)",
              color: "#fff",
              border: "none",
              fontSize: "12.5px",
              fontWeight: 600,
              cursor: "pointer"
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
